import { useParams, Link } from 'react-router-dom'

import {
	Circle,
	Package,
	CircleUser,
	CircleDollarSign,
	HandCoins,
	Landmark,
	ChartCandlestick,
	DollarSign
} from 'lucide-react'

import { StatsCard } from '@/components/shared/StatsCard'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'

import { useCoinDetails } from '@/hooks/useApi'

export const CoinDetailsPage = () => {
	const { id } = useParams<{ id: string }>()
	if (!id) {
		return <>Not Found</>
	}

	const { data: coinDetails } = useCoinDetails(id);

	if (!coinDetails) {
		return <>Loading...</>
	}

	return (
		<div className="flex flex-col overflow-hidden w-full h-full">
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className="flex flex-col w-full gap-2 px-2 lg:px-4 pt-4">
					<h1 className="scroll-m-20 text-md lg:text-2xl font-bold tracking-tight">
						Coin Details
					</h1>

					<div className='grid grid-cols-1 md:grid-cols-7 gap-2 items-start'>
						<div className='grid grid-cols-1 md:col-span-4 gap-2'>
							<div>
								<StatsCard
									className='text-sm'
									valueStyleOverride='text-lg lg:text-xl overflow-hidden whitespace-nowrap text-ellipsis'
									title="Coin"
									value={`${coinDetails?.name} :: ${coinDetails?.symbol}`}
									icon={<Circle className="text-primary h-4 w-4" />}
								/>
							</div>
							<div>
								<StatsCard
									title="Package ID"
									className='text-sm'
									valueStyleOverride='text-lg lg:text-xl overflow-hidden whitespace-nowrap text-ellipsis'
									value={
										<Link to={`/packages/${coinDetails.packageId}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
											{coinDetails.packageId}
										</Link>
									}
									icon={<Package className="text-primary h-4 w-4" />}
								/>
							</div>
							<div>
								<StatsCard
									title="Publisher Address"
									className='text-sm'
									valueStyleOverride='text-lg lg:text-xl overflow-hidden whitespace-nowrap text-ellipsis'
									value={
										<Link to={`/accounts/${coinDetails.publisherAddress}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
											{coinDetails.publisherAddress}
										</Link>
									}
									icon={<CircleUser className="text-primary h-4 w-4" />}
								/>
							</div>
						</div>
						<div className='md:col-span-3'>
							<Card className='bg-transparent flex-1'>
								<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
									<div className='flex flex-row items-center px-2 pt-2'>
										<div className="rounded-full px-2 transition-colors">
											<ChartCandlestick className='h-5 w-5' />
										</div>
										<div className='flex-1'>
											<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Market Data</CardTitle>
										</div>
										<div className='flex flex-col rounded-full p-2 transition-colors hover:bg-muted'>
										</div>
									</div>
									<div className='px-0'>
										<CardDescription className='flex flex-row px-4 py-1 text-muted-foreground text-xs lg:text-md gap-1'>
											<span>Data provided by</span>
											<a href="https://www.coingecko.com/" target="_blank" rel="noopener noreferrer" className='text-[#4DA2FF] hover:text-[#C0E6FF]'>
												CoinGecko
											</a>
										</CardDescription>
									</div>

								</CardHeader>
								<CardContent className='px-4 pb-4'>
									<div className='flex flex-col gap-3'>
										<StatsCard
											title="Price (USD)"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={`$${coinDetails?.marketData?.currentPrice}`}
											icon={<DollarSign className="h-5 w-5" />}
										/>

										<StatsCard
											title="Market Capitalization (USD)"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={coinDetails?.marketData?.marketCapitalizationUsd ? `$${Number(coinDetails?.marketData?.marketCapitalizationUsd).toLocaleString()}` : "--"}
											icon={<CircleDollarSign className="text-primary" />}
										/>
										<StatsCard
											title="Circulating Supply"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={coinDetails?.marketData?.circulatingSupply ? Number(coinDetails?.marketData?.circulatingSupply).toLocaleString() : "--"}
											icon={<HandCoins className="text-primary" />}
										/>
										<StatsCard
											title="Total Supply"
											className='text-sm'
											valueStyleOverride='text-md xl:text-xl'
											value={coinDetails?.marketData?.totalSupply ? Number(coinDetails?.marketData?.totalSupply).toLocaleString() : "--"}
											icon={<Landmark className="text-primary" />}
										/>
									</div>
								</CardContent>
							</Card>
						</div>


					</div>
				</div>
			</div>
		</div>
	)
}